import React from 'react';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const EventPreview = ({ formData, onClose }) => {
  const formatDate = (dateString, timeString) => {
    if (!dateString) return 'Date not set';
    const date = new Date(dateString);
    const time = timeString ? ` at ${timeString}` : '';
    return date?.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }) + time;
  };

  const formatPrice = (price) => {
    return price ? `$${parseFloat(price)?.toFixed(2)}` : 'Free';
  };

  const getLocationString = () => {
    if (formData?.venueType === 'virtual') {
      return `Virtual Event via ${formData?.platform || 'Online Platform'}`;
    } else if (formData?.venueType === 'hybrid') {
      return `Hybrid Event - ${formData?.venueName || 'Venue'} + Virtual`;
    } else {
      return `${formData?.venueName || 'Venue Name'}, ${formData?.city || 'City'}, ${formData?.state || 'State'}`;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-border p-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">Event Preview</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-6">
          {/* Event Header */}
          <div className="mb-8">
            {formData?.image && (
              <div className="w-full h-64 mb-6 overflow-hidden rounded-lg">
                <Image
                  src={formData?.image}
                  alt={formData?.title || 'Event'}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-1 bg-primary text-primary-foreground text-xs font-medium rounded">
                    {formData?.category || 'Category'}
                  </span>
                  {formData?.visibility === 'private' && (
                    <span className="px-2 py-1 bg-warning text-warning-foreground text-xs font-medium rounded">
                      Private
                    </span>
                  )}
                </div>

                <h1 className="text-3xl font-bold text-foreground mb-4">
                  {formData?.title || 'Event Title'}
                </h1>

                <div className="space-y-2 text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Icon name="Calendar" size={16} />
                    <span>{formatDate(formData?.startDate, formData?.startTime)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="MapPin" size={16} />
                    <span>{getLocationString()}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Users" size={16} />
                    <span>
                      {formData?.capacity || formData?.virtualCapacity || 'Unlimited'} attendees max
                    </span>
                  </div>
                </div>
              </div>

              <div className="lg:w-80">
                <div className="bg-muted p-4 rounded-lg">
                  <h3 className="font-semibold text-foreground mb-3">Ticket Pricing</h3>
                  <div className="space-y-2">
                    {formData?.tiers && formData?.tiers?.length > 0 ? (
                      formData?.tiers?.map((tier, index) => (
                        <div key={tier?.id || index} className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">{tier?.name}</span>
                          <span className="font-medium text-foreground">
                            {tier?.earlyBird && tier?.earlyBirdPrice 
                              ? `${formatPrice(tier?.earlyBirdPrice)} (Early)`
                              : formatPrice(tier?.price)
                            }
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-muted-foreground">No tiers configured</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Event Description */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">About This Event</h2>
            <div className="prose max-w-none">
              <p className="text-muted-foreground whitespace-pre-wrap">
                {formData?.description || 'No description provided.'}
              </p>
            </div>
          </div>

          {/* Ticket Tiers Details */}
          {formData?.tiers && formData?.tiers?.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Ticket Options</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {formData?.tiers?.map((tier, index) => (
                  <div key={tier?.id || index} className="border border-border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-medium text-foreground">{tier?.name}</h3>
                      <div className="text-right">
                        <div className="font-semibold text-foreground">
                          {formatPrice(tier?.price)}
                        </div>
                        {tier?.earlyBird && tier?.earlyBirdPrice && (
                          <div className="text-sm text-success">
                            Early: {formatPrice(tier?.earlyBirdPrice)}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {tier?.description && (
                      <p className="text-sm text-muted-foreground mb-3">{tier?.description}</p>
                    )}
                    
                    {tier?.benefits && tier?.benefits?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-foreground mb-2">Includes:</h4>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          {tier?.benefits?.map((benefit, benefitIndex) => (
                            <li key={benefitIndex} className="flex items-center gap-2">
                              <Icon name="Check" size={12} className="text-success" />
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <div className="mt-3 text-xs text-muted-foreground">
                      {tier?.capacity} tickets available
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Venue Information */}
          {(formData?.venueType === 'physical' || formData?.venueType === 'hybrid') && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Venue Details</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-foreground mb-2">{formData?.venueName}</h3>
                  <div className="text-muted-foreground space-y-1">
                    <p>{formData?.address}</p>
                    <p>{formData?.city}, {formData?.state} {formData?.zipCode}</p>
                    {formData?.parking && (
                      <p className="text-sm">Parking: {formData?.parking}</p>
                    )}
                  </div>
                  
                  {formData?.accessibilityFeatures && formData?.accessibilityFeatures?.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-foreground mb-2">Accessibility:</h4>
                      <div className="flex flex-wrap gap-2">
                        {formData?.accessibilityFeatures?.map((feature) => (
                          <span key={feature} className="px-2 py-1 bg-accent text-accent-foreground text-xs rounded">
                            {feature?.replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="w-full h-64 bg-muted rounded-lg border border-border overflow-hidden">
                  <iframe
                    width="100%"
                    height="100%"
                    loading="lazy"
                    title="Venue Location"
                    referrerPolicy="no-referrer-when-downgrade"
                    src="https://www.google.com/maps?q=37.7749,-122.4194&z=14&output=embed"
                    className="border-0"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Discount Codes */}
          {formData?.discountCodes && formData?.discountCodes?.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Available Discounts</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {formData?.discountCodes?.map((code, index) => (
                  <div key={code?.id || index} className="border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-sm bg-muted px-2 py-1 rounded">
                        {code?.code}
                      </span>
                      <span className="font-semibold text-success">
                        {code?.type === 'percentage' ? `${code?.value}% OFF` : `$${code?.value} OFF`}
                      </span>
                    </div>
                    {code?.description && (
                      <p className="text-sm text-muted-foreground">{code?.description}</p>
                    )}
                    <div className="text-xs text-muted-foreground mt-2">
                      {code?.maxUses && `Max ${code?.maxUses} uses`}
                      {code?.validUntil && ` • Valid until ${new Date(code.validUntil)?.toLocaleDateString()}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventPreview;